import React, { useEffect, useState, useCallback } from "react";
import {
  Button, Typography, IconButton, Tooltip
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DeleteIcon from "@material-ui/icons/Delete";
import NotificationsActiveIcon from "@material-ui/icons/NotificationsActive";
import NotificationsOffIcon from "@material-ui/icons/NotificationsOff";
import { toast } from "react-toastify";
import MainContainer from "../../components/MainContainer";
import PageHeader from "../../components/PageHeader";
import DataTable from "../../components/DataTable";
import StatusChip from "../../components/StatusChip";
import api from "../../services/api";
import useConfirm from "../../hooks/useConfirm";
import {
  isPushSupported, enablePushNotifications, disablePushNotifications,
  getCurrentPushSubscription
} from "../../services/push";

const formatDate = (s) => { try { return s ? new Date(s).toLocaleString() : "—"; } catch (e) { return s || "—"; } };

// "granted"/"denied"/"default" sao termos da API do navegador, nao do produto.
const PERMISSION_LABEL = { granted: "concedida", denied: "negada", default: "não solicitada" };
const PERMISSION_TONE = { granted: "success", denied: "danger", default: "warning" };

const useStyles = makeStyles(() => ({
  device: { wordBreak: "break-word" },
  date: { fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
}));

const PushSettings = () => {
  const classes = useStyles();
  const [confirm, confirmDialog] = useConfirm();
  const [supported] = useState(isPushSupported());
  const [permission, setPermission] = useState(typeof Notification !== "undefined" ? Notification.permission : "default");
  const [active, setActive] = useState(false);
  const [subs, setSubs] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/push/subscriptions");
      setSubs(Array.isArray(data) ? data : []);
    } catch (e) { /* silent */ }
    try {
      const cur = await getCurrentPushSubscription();
      setActive(!!cur);
    } catch (e) { /* */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const enable = async () => {
    const r = await enablePushNotifications();
    if (!r.ok) {
      toast.error(`Não foi possível ativar (${r.reason})`);
    } else {
      toast.success("Notificações ativadas neste dispositivo");
      setPermission("granted");
      setActive(true);
      load();
    }
  };

  const disable = async () => {
    await disablePushNotifications();
    toast.info("Notificações desativadas neste dispositivo");
    setActive(false);
    load();
  };

  const sendTest = async () => {
    try {
      await api.post("/push/test");
      toast.success("Notificação de teste enviada");
    } catch (e) { toast.error("Falha ao enviar teste"); }
  };

  const removeSub = async (sub) => {
    if (!(await confirm({ title: "Remover esta inscrição?", message: "O dispositivo deixa de receber notificações push." }))) return;
    try {
      await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
      load();
    } catch (e) { toast.error("Erro ao remover"); }
  };

  const columns = [
    {
      key: "device",
      label: "Dispositivo",
      render: sub => (
        <Typography variant="subtitle2" className={classes.device}>
          {sub.userAgent || `${sub.endpoint.slice(0, 60)}…`}
        </Typography>
      ),
    },
    { key: "createdAt", label: "Inscrito em", render: sub => <span className={classes.date}>{formatDate(sub.createdAt)}</span> },
    { key: "lastUsedAt", label: "Último uso", render: sub => <span className={classes.date}>{formatDate(sub.lastUsedAt)}</span> },
    {
      key: "actions",
      label: "Ações",
      type: "actions",
      render: sub => (
        <Tooltip title="Remover inscrição">
          <IconButton size="small" onClick={() => removeSub(sub)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <MainContainer>
      {confirmDialog}
      <PageHeader
        title="Notificações Push"
        count={subs.length}
        subtitle="Avisos no navegador quando chega mensagem — por dispositivo, não por conta."
        actions={
          supported ? (
            <>
              <Button variant="outlined" onClick={sendTest} disabled={!active}>
                Enviar teste
              </Button>
              {!active ? (
                <Button startIcon={<NotificationsActiveIcon />} variant="contained" color="primary" onClick={enable}>
                  Ativar neste dispositivo
                </Button>
              ) : (
                <Button startIcon={<NotificationsOffIcon />} variant="outlined" onClick={disable}>
                  Desativar
                </Button>
              )}
            </>
          ) : null
        }
        filters={
          supported ? (
            <>
              <StatusChip dot tone={PERMISSION_TONE[permission] || "neutral"}
                label={`Permissão do navegador: ${PERMISSION_LABEL[permission] || permission}`} />
              <StatusChip dot tone={active ? "success" : "neutral"}
                label={active ? "Ativo neste dispositivo" : "Inativo neste dispositivo"} />
            </>
          ) : (
            <StatusChip dot tone="danger" label="Este navegador não suporta Web Push" />
          )
        }
      />

      <DataTable
        columns={columns}
        rows={subs}
        emptyIcon={<NotificationsActiveIcon />}
        emptyTitle="Nenhum dispositivo inscrito"
        emptyDescription="Ative as notificações em cada navegador que deve receber os avisos — a inscrição é por dispositivo."
        emptyAction={
          supported && !active ? (
            <Button startIcon={<NotificationsActiveIcon />} variant="contained" color="primary" onClick={enable}>
              Ativar neste dispositivo
            </Button>
          ) : null
        }
      />
    </MainContainer>
  );
};

export default PushSettings;
